import React, { createContext, useReducer, useEffect, Reducer, FC } from 'react';
import { reducerWithLogger } from './util';
import notifications, { Notification, BannerNotification, NotificationResponse } from './notifications';

export enum Status {
  Unread = 2,
  Read = 1,
  Deleted = 0
}

enum Action {
  FetchAllRequest = 'NOTIFICATIONS:FETCH_ALL_REQUEST',
  FetchAllSuccess = 'NOTIFICATIONS:FETCH_ALL_SUCCESS',
  FetchAllFailure = 'NOTIFICATIONS:FETCH_ALL_FAILURE',
  ReadOneRequest = 'NOTIFICATIONS:READ_ONE_REQUEST',
  ReadOneSuccess = 'NOTIFICATIONS:READ_ONE_SUCCESS',
  ReadOneFailure = 'NOTIFICATIONS:READ_ONE_FAILURE',
  DeleteAllRequest = 'NOTIFICATIONS:DELETE_ALL_REQUEST',
  DeleteAllSuccess = 'NOTIFICATIONS:DELETE_ALL_SUCCESS',
  DeleteAllFailure = 'NOTIFICATIONS:DELETE_ALL_FAILURE',
  DeleteOneRequest = 'NOTIFICATIONS:DELETE_ONE_REQUEST',
  DeleteOneSuccess = 'NOTIFICATIONS:DELETE_ONE_SUCCESS',
  DeleteOneFailure = 'NOTIFICATIONS:DELETE_ONE_FAILURE'
}

interface FetchAllRequestAction {
  type: Action.FetchAllRequest;
}

interface FetchAllSuccessAction {
  type: Action.FetchAllSuccess;
  data: NotificationResponse;
}

interface FetchAllFailureAction {
  type: Action.FetchAllFailure;
  error: Error;
}

interface ReadOneRequestAction {
  type: Action.ReadOneRequest;
}

interface ReadOneSuccessAction {
  type: Action.ReadOneSuccess;
  id: string;
}

interface ReadOneFailureAction {
  type: Action.ReadOneFailure;
  error: Error;
}

interface DeleteAllRequestAction {
  type: Action.DeleteAllRequest;
}

interface DeleteAllSuccessAction {
  type: Action.DeleteAllSuccess;
}

interface DeleteAllFailureAction {
  type: Action.DeleteAllFailure;
  error: Error;
}

interface DeleteOneRequestAction {
  type: Action.DeleteOneRequest;
}

interface DeleteOneSuccessAction {
  type: Action.DeleteOneSuccess;
  id: string;
}

interface DeleteOneFailureAction {
  type: Action.DeleteOneFailure;
  error: Error;
}

type NotificationAction =
  FetchAllRequestAction |
  FetchAllSuccessAction |
  FetchAllFailureAction |
  ReadOneRequestAction |
  ReadOneSuccessAction |
  ReadOneFailureAction |
  DeleteAllRequestAction |
  DeleteAllSuccessAction |
  DeleteAllFailureAction |
  DeleteOneRequestAction |
  DeleteOneSuccessAction |
  DeleteOneFailureAction;

interface NotificationState {
  loading: boolean;
  ready: boolean;
  error?: Error;
  notifications?: Notification[];
  banners?: BannerNotification[];
  unreadCount?: number;
}

type NotificationReducer = Reducer<NotificationState, NotificationAction>;

type NotificationContextProvider = NotificationState & {
  fetchAll: () => Promise<void>;
  deleteOne: (id: string) => Promise<void>;
  deleteAll: () => Promise<void>;
  readOne: (id: string, url: string) => Promise<string>;
}

const initialState: NotificationState = {
  loading: false,
  ready: false,
  error: null,
  notifications: null,
  banners: null,
  unreadCount: null,
};

const NotificationContext = createContext<Partial<NotificationContextProvider>>({});

const reducer = reducerWithLogger((state: NotificationState, action: NotificationAction): NotificationState => {
  const getUnreadCount = (items: Notification[]): number =>
    items.filter(i => i.status === Status.Unread).length;

  if (action.type === Action.ReadOneSuccess) {
    const mapStatusRead = <T extends Notification>(items: T[]): T[] =>
      items.map(i => {
        if (i.id === action.id) i.status = Status.Read;
        return i;
      });

    const n = mapStatusRead<Notification>(state.notifications);
    const b = mapStatusRead<BannerNotification>(state.banners);

    return {
      ...state,
      loading: false,
      error: null,
      notifications: n,
      banners: b,
      unreadCount: getUnreadCount(n),
    };
  }

  if (action.type === Action.DeleteOneSuccess) {
    const n = state.notifications.filter(n => n.id !== action.id);
    const b = state.banners.filter(n => n.id !== action.id);

    return {
      ...state,
      loading: false,
      error: null,
      notifications: n,
      banners: b,
      unreadCount: getUnreadCount(n),
    };
  }

  if (action.type === Action.FetchAllSuccess) {
    return {
      ...state,
      loading: false,
      error: null,
      notifications: action.data.regularNotifications,
      banners: action.data.bannerNotifications,
      unreadCount: getUnreadCount(action.data.regularNotifications),
    };
  }

  switch (action.type) {
    case Action.FetchAllRequest:
    case Action.DeleteAllRequest:
    case Action.DeleteOneRequest:
    case Action.ReadOneRequest:
      return {
        ...state,
        loading: true,
      };

    case Action.DeleteAllFailure:
    case Action.DeleteOneFailure:
    case Action.FetchAllFailure:
    case Action.ReadOneFailure:
      return {
        ...state,
        error: action.error,
        loading: false,
      };

    case Action.DeleteAllSuccess:
      return {
        ...state,
        loading: false,
        error: null,
        notifications: [],
        unreadCount: 0,
      };

    default:
      return state;
  }
});

const NotificationProvider: FC = (props) => {
  const [state, dispatch] = useReducer<NotificationReducer>(reducer, initialState);

  const fetchAll: NotificationContextProvider['fetchAll'] = async function() {
    dispatch({ type: Action.FetchAllRequest });

    try {
      const data = await notifications.getAll();
      dispatch({ type: Action.FetchAllSuccess, data });
    } catch (error) {
      dispatch({ type: Action.FetchAllFailure, error });
    }
  };

  const deleteOne: NotificationContextProvider['deleteOne'] = async function(id) {
    dispatch({ type: Action.DeleteOneRequest });

    try {
      await notifications.deleteOne(id);
      dispatch({ type: Action.DeleteOneSuccess, id });
    } catch (error) {
      dispatch({ type: Action.DeleteOneFailure, error });
      throw error;
    }
  };

  const deleteAll: NotificationContextProvider['deleteAll'] = async function() {
    dispatch({ type: Action.DeleteAllRequest });

    try {
      await notifications.deleteAll();
      dispatch({ type: Action.DeleteAllSuccess });
    } catch (error) {
      dispatch({ type: Action.DeleteAllFailure, error });
      throw error;
    }
  };

  const readOne: NotificationContextProvider['readOne'] = async function(id, url) {
    dispatch({ type: Action.ReadOneRequest });

    try {
      const { redirectUrl } = await notifications.readOne(id, url);
      dispatch({ type: Action.ReadOneSuccess, id });
      return redirectUrl;
    } catch (error) {
      dispatch({ type: Action.ReadOneFailure, error });
      throw error;
    }
  };

  useEffect(() => {
    (async (): Promise<void> => await fetchAll())();
  }, []);

  const value = {
    ...state,
    deleteOne,
    deleteAll,
    readOne,
  };

  return (
    <NotificationContext.Provider value={value}>
      {props.children}
    </NotificationContext.Provider>
  );
};
NotificationProvider.displayName = 'NotificationProvider';

export {
  NotificationContext as default,
  NotificationProvider
};
